import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { platform } from '@/platform';
import { toast } from 'sonner';
import type { AppNotification } from '@/lib/notifications/types';

export type { AppNotification };


/** Unique per subscription; randomUUID is missing on old WebViews. */
function newChannelSuffix(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
}

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  // Mirrors `notifications` synchronously so the realtime handler below can
  // check for an id without closing over a stale list (its effect only
  // resubscribes when `user`/`fetchNotifications` change).
  const notificationsRef = useRef<AppNotification[]>([]);
  notificationsRef.current = notifications;

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('notifications')
      .select('id, type, title, message, metadata, read, created_at, link')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(50);

    if (data) {
      // Normalise: prefer `body` column, fall back to legacy `message`
      const normalised = data.map(normalise);
      setNotifications(normalised);
      setUnreadCount(normalised.filter((n) => !n.read).length);
    }

    // The badge counts UNREAD, but the list above is capped at 50 — past 50
    // notifications the unread count silently truncated (#673). Count unread
    // server-side; the capped list stays as the list.
    const { count, error: countError } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('read', false);
    // Only overwrite on success — a failed count must never zero the badge.
    if (!countError && typeof count === 'number') setUnreadCount(count);

    setLoading(false);
  }, [user]);

  /**
   * Realtime UPDATE/DELETE echoes arrive one per row. "Mark all read" on N
   * unread rows used to start N list+count refetches (2N queries, more when
   * the bell and the page were both mounted). Echoes now fold into one
   * refetch per burst, and a refetch requested while one is running runs once
   * more after it rather than in parallel (QA #1279 follow-up, MC-03).
   */
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refetchInFlightRef = useRef(false);
  const refetchAgainRef = useRef(false);
  const fetchRef = useRef(fetchNotifications);
  fetchRef.current = fetchNotifications;

  const runCoalescedRefetch = useCallback(async () => {
    if (refetchInFlightRef.current) { refetchAgainRef.current = true; return; }
    refetchInFlightRef.current = true;
    try {
      do {
        refetchAgainRef.current = false;
        await fetchRef.current();
      } while (refetchAgainRef.current);
    } finally {
      refetchInFlightRef.current = false;
    }
  }, []);

  const scheduleRefetch = useCallback(() => {
    if (refetchTimerRef.current !== null) return;
    refetchTimerRef.current = setTimeout(() => {
      refetchTimerRef.current = null;
      void runCoalescedRefetch();
    }, REFETCH_COALESCE_MS);
  }, [runCoalescedRefetch]);

  useEffect(() => () => {
    if (refetchTimerRef.current !== null) clearTimeout(refetchTimerRef.current);
    refetchTimerRef.current = null;
  }, []);

  // Real-time subscription
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    fetchNotifications();

    // Clean up any previous channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // A name of its own per subscription. The Notifications page and the
    // Header's bell both use this hook; with a shared name, supabase-js hands
    // the second caller the first one's already-subscribed channel, and
    // .on('postgres_changes') then throws ("cannot add postgres_changes
    // callbacks … after subscribe()"), crashing /notifications. Rows are
    // scoped by the filter below, not by the channel name.
    const channel = supabase
      .channel(`notifications:${user.id}:${newChannelSuffix()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === 'UPDATE') {
            // The echo of a change this list already shows (our own "mark
            // all read", or markRead after its refetch) changes nothing.
            const row = payload.new as RawNotificationRow | undefined;
            const local = row?.id ? notificationsRef.current.find((n) => n.id === row.id) : undefined;
            if (local && row && isSameAsShown(local, normalise(row))) return;
            scheduleRefetch();
            return;
          }
          if (payload.eventType === 'DELETE') {
            scheduleRefetch();
            return;
          }
          const notif = normalise(payload.new as RawNotificationRow);
          // A foreground push (below) can trigger a refetch that loads this
          // same row before its delayed realtime event arrives. Without this
          // check the notification was prepended — and unreadCount
          // incremented — a second time.
          if (notificationsRef.current.some((n) => n.id === notif.id)) return;
          notificationsRef.current = [notif, ...notificationsRef.current];
          setNotifications(notificationsRef.current);
          if (!notif.read) setUnreadCount((prev) => prev + 1);
          if (announceOnce(notif.id)) showBrowserToast(notif);
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user, fetchNotifications, scheduleRefetch]);

  // A push that lands while the app is open (M8). The OS shows nothing in the
  // foreground, so this is what surfaces it: the list and badge refetch, and
  // the toast shows once — whichever of push and realtime arrives first wins,
  // keyed by the notification id both carry. Push does not depend on the
  // realtime publication, so this also covers a table realtime cannot see.
  // A no-op subscription on the web.
  useEffect(() => {
    if (!user) return;
    return platform.push.onMessage((message) => {
      void fetchNotifications();
      if (!announceOnce(message.eventId)) return;
      showBrowserToast({
        id: message.eventId,
        type: 'push',
        title: message.title ?? 'Kanfy',
        body: message.body ?? '',
        data: {},
        read: false,
        created_at: new Date().toISOString(),
        link: message.link ?? null,
      });
    });
  }, [user, fetchNotifications]);

  const markRead = useCallback(async (notificationId: string) => {
    if (!user) return;
    // A zero-row conditional write can mean another device already read it.
    // Read the authoritative list/count after success; realtime echoes use the
    // same reconciliation, so they cannot consume the badge a second time.
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId)
      .eq('user_id', user.id)
      .eq('read', false)
      .select('id');
    if (!error) await fetchNotifications();
  }, [user, fetchNotifications]);

  const markAllRead = useCallback(async () => {
    if (!user) return;
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false);

    if (!error) {
      // The ref first: the realtime echoes of this write can arrive before
      // React re-renders, and they are recognised against the ref.
      notificationsRef.current = notificationsRef.current.map((n) => ({ ...n, read: true }));
      setNotifications(notificationsRef.current);
      setUnreadCount(0);
    }
  }, [user]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (!error) {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setUnreadCount((prev) => {
        const deleted = notifications.find((n) => n.id === notificationId);
        return deleted && !deleted.read ? Math.max(0, prev - 1) : prev;
      });
    }
  }, [notifications]);

  const sendNotification = useCallback(
    async (payload: {
      type: string;
      recipientId: string;
      recipientEmail?: string;
      recipientPhone?: string;
      recipientName?: string;
      data?: Record<string, unknown>;
      channels?: Array<'in_app' | 'email' | 'sms' | 'push'>;
    }) => {
      const { data, error } = await supabase.functions.invoke('send-notification', {
        body: {
          type: payload.type,
          recipient_id: payload.recipientId,
          recipient_email: payload.recipientEmail,
          recipient_phone: payload.recipientPhone,
          recipient_name: payload.recipientName,
          data: payload.data ?? {},
          channels: payload.channels,
        },
      });
      return { data, error };
    },
    []
  );

  return {
    notifications,
    unreadCount,
    loading,
    markRead,
    markAllRead,
    deleteNotification,
    sendNotification,
    refresh: fetchNotifications,
  };
}

// ── Helpers ─────────────────────────────────────────────────────

/** Window in which a burst of realtime echoes folds into one refetch. */
export const REFETCH_COALESCE_MS = 250;

/** True when an UPDATE echo carries nothing the list does not already show. */
function isSameAsShown(shown: AppNotification, incoming: AppNotification): boolean {
  // Every field the list renders or sorts by: type picks the icon,
  // created_at orders and groups, data feeds links (Codex on #1303).
  return shown.read === incoming.read
    && shown.type === incoming.type
    && shown.created_at === incoming.created_at
    && shown.title === incoming.title
    && shown.body === incoming.body
    && shown.link === incoming.link
    && JSON.stringify(shown.data) === JSON.stringify(incoming.data);
}

/** Raw notification row as returned from the DB select or realtime payload. */
type RawNotificationRow = {
  id: string;
  type: string;
  title: string;
  message?: string | null;
  metadata?: unknown;
  read?: boolean | null;
  created_at: string;
  link?: string | null;
};

function normalise(row: RawNotificationRow): AppNotification {
  return {
    id: row.id,
    type: row.type,
    title: row.title,
    body: row.message ?? '',
    data: (row.metadata ?? {}) as Record<string, unknown>,
    read: row.read ?? false,
    created_at: row.created_at,
    link: row.link ?? null,
  };
}

/**
 * Ids already announced with a toast, shared by every hook instance: one
 * notification reaches the app by realtime AND by push, and should be
 * announced once. Bounded; the oldest id is forgotten first.
 */
const announced = new Set<string>();
const ANNOUNCED_LIMIT = 200;

export function announceOnce(id: string): boolean {
  if (!id || announced.has(id)) return false;
  announced.add(id);
  if (announced.size > ANNOUNCED_LIMIT) {
    const oldest = announced.values().next().value;
    if (oldest !== undefined) announced.delete(oldest);
  }
  return true;
}

function showBrowserToast(notif: AppNotification) {
  toast(notif.title, {
    description: notif.body,
    duration: 5000,
    action: notif.link
      ? { label: 'View', onClick: () => window.location.assign(notif.link!) }
      : undefined,
  });
}
